import { IconButton, Stack, Tooltip } from '@mui/material'
import { ReactComponent as AnalyticsDownloadLogo } from 'assets/analytics_download_logo.svg'
import fileSaver from 'file-saver'
import { useGetDailyShifts } from 'hook/query/useGetDailyShifts'
import moment, { Moment } from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CattlesApiService from 'services/cattlesApiServices'
import { COLORS } from 'styles/colors'

import Input from 'components/shared/textFields/Input'
import { DateRestriction } from 'ui/DatePickerInput/types'
import BreadCrumbsGroup from 'ui/breadCrumbs/BreadCrumbsGroup'

import DatePicker from 'ui/DatePickerInput/DatePicker'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { ShiftType } from '../types'
import { IMilkingCowItem } from './IMilkingTableResponse'

interface TableTabsProps {
	data: IMilkingCowItem[]
	selectedBarn: number | null
	selectedGroup: number[]
	selectedTags: number[]
	setSelectedTags: React.Dispatch<React.SetStateAction<number[]>>
	selectedDate: string
	setSelectedDate: React.Dispatch<React.SetStateAction<string>>
	setSelectedShift: React.Dispatch<React.SetStateAction<string | number>>
	selectedShift: string | number
	onChangeInput: (e: React.ChangeEvent<HTMLInputElement>) => void
	inputValue: string
}

function TableTabs({
	selectedBarn,
	selectedGroup,
	selectedTags,
	selectedDate,
	setSelectedDate,
	setSelectedShift,
	selectedShift,
	onChangeInput,
	inputValue,
}: TableTabsProps) {
	const [isExporting, setIsExporting] = useState(false)

	const { data: dailyShifts = [] } = useGetDailyShifts({})

	const shiftOptions = useMemo(
		() =>
			dailyShifts.map(shift => ({
				id: shift.value,
				title: shift.title,
				selected: shift.value === selectedShift,
			})),
		[dailyShifts, selectedShift],
	)

	useEffect(() => {
		if (selectedShift === ShiftType.ALL || dailyShifts.length === 0) return

		const isShiftAvailable = dailyShifts.some(
			shift => shift.value === selectedShift,
		)
		if (!isShiftAvailable) {
			setSelectedShift(ShiftType.ALL)
		}
	}, [dailyShifts, selectedShift, setSelectedShift])

	const handleExportToExcel = useCallback(async () => {
		if (isExporting) return

		setIsExporting(true)
		try {
			const response = await CattlesApiService.getMilkYieldTableExport({
				date: selectedDate,
				barn_id: selectedBarn ?? undefined,
				...(selectedGroup.length > 0 && {
					group_id: selectedGroup.join(','),
				}),
				period:
					selectedShift === ShiftType.ALL
						? undefined
						: (selectedShift as Exclude<ShiftType, ShiftType.ALL>),
				tag_id: selectedTags,
				tag_id_search: inputValue,
			})

			const blob = new Blob([response.data], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			})
			fileSaver.saveAs(
				blob,
				`Доение_по_сменам_${moment(selectedDate).format('DD.MM.YYYY')}.xlsx`,
			)
		} catch (error) {
			void error
		} finally {
			setIsExporting(false)
		}
	}, [
		inputValue,
		isExporting,
		selectedBarn,
		selectedDate,
		selectedGroup,
		selectedShift,
		selectedTags,
	])

	return (
		<Stack direction='column' gap='16px'>
			<Stack
				direction='row'
				alignItems='center'
				justifyContent='space-between'
				sx={{ mb: 3 }}
			>
				<TypographyHeader>Доение по сменам</TypographyHeader>
				<Stack direction='row' alignItems='center' gap='16px'>
					<Input
						width={'400px'}
						value={inputValue}
						onChange={onChangeInput}
						label={'Поиск по номеру бирки'}
						size='small'
					/>
					<Tooltip
						title='Экспорт доения по сменам'
						placement='bottom-end'
						arrow
						componentsProps={{
							tooltip: {
								sx: {
									backgroundColor: COLORS.white,
									color: COLORS.secondaryFont,
									fontSize: '14px',
									fontWeight: 500,
									px: '12px',
									py: '8px',
									borderRadius: '8px',
									boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
								},
							},
							arrow: {
								sx: {
									color: COLORS.white,
									'&:before': {
										boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
									},
								},
							},
						}}
					>
						<IconButton
							size='small'
							onClick={handleExportToExcel}
							disabled={isExporting}
							sx={{
								color: COLORS.icon,
								borderRadius: '8px',
								'&:hover': {
									color: COLORS.secondary,
									backgroundColor: COLORS.background,
								},
							}}
						>
							<AnalyticsDownloadLogo stroke={COLORS.icon} />
						</IconButton>
					</Tooltip>
					{/* <AutocompleteInput
						label='Поиск по номеру бирки'
						widthForInput='400px'
						isCheckbox
						startIcon={<SearchIcon stroke={COLORS.icon} />}
						availableList={availableTagsData?.availableTagsOption}
						selectedValues={selectedTags}
						onClick={handleTagToggle}
						isShowTags={false}
						loading={isLoading}
					/> */}
				</Stack>
			</Stack>
			<BreadCrumbsGroup
				array={shiftOptions}
				handleActiveFilter={setSelectedShift}
				allLabel='За день'
			/>
			<DatePicker
				value={selectedDate}
				onChange={(date: Moment | null) =>
					date && setSelectedDate(date.format('YYYY-MM-DD'))
				}
				isTodayFilter
				isYesterdayFilter
				restrictionType={DateRestriction.PAST}
			/>
		</Stack>
	)
}

export default TableTabs
